import { apiClient } from './client';

/**
 * Download a list endpoint's CSV export **through the axios client** so the JWT
 * `Authorization` header is attached (F8 — the access token is in-memory, so a
 * raw `window.location` navigation to the API URL 401s).
 *
 * @param path   API path relative to the client baseURL, e.g. `/crm/leads/`.
 * @param params Live list filters; `export: 'csv'` is added automatically.
 */
export async function exportCsv(
  path: string,
  params: Record<string, unknown> = {},
): Promise<void> {
  const { data, headers } = await apiClient.get<Blob>(path, {
    params: { ...params, export: 'csv' },
    responseType: 'blob',
  });

  // Prefer the server's Content-Disposition filename (CsvExportMixin sets it,
  // e.g. `my-salary.csv`); otherwise derive an entity name from the path
  // (`/cabinet/salary/` → `salary-export.csv`) rather than a generic `export.csv`.
  const disposition = (headers['content-disposition'] as string | undefined) ?? '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const segment = path.split('?')[0].split('/').filter(Boolean).pop() ?? 'export';
  const filename = match ? decodeURIComponent(match[1]) : `${segment}-export.csv`;

  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download any binary endpoint (e.g. a generated PDF) through the axios client
 * so auth headers are attached. Uses the server's Content-Disposition filename
 * when present, else `fallbackName`.
 *
 * @param path         API path relative to baseURL, e.g. `/projects/worklogs/export-pdf/`.
 * @param params       Query params (live filters).
 * @param fallbackName Name to save as when the server sends no filename.
 */
export async function downloadFile(
  path: string,
  params: Record<string, unknown> = {},
  fallbackName = 'download',
): Promise<void> {
  const { data, headers } = await apiClient.get<Blob>(path, { params, responseType: 'blob' });
  const disposition = (headers['content-disposition'] as string | undefined) ?? '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
